/**
 * L0 estate pulse (dashboard design rules §7): the last 14 days as a single
 * `StatusStrip` row, each cell the WORST status any monitored website recorded
 * that day — one glance answers "did anything break recently?".
 *
 * Data source: the websites daily rollups (`/monitoring/history`) — the only
 * daily history NOVA exposes today. Cloud and M365 have no per-day endpoint
 * yet, so the pulse is honest about being website-scoped in its caption.
 *
 * Honesty (§5): a day with no recorded checks is `neutral` ("no data"), never
 * green — and the coverage line states how many of the 14 days are recorded.
 *
 * WORKAROUND (design-system gap, same as `features/dashboard/uptime-heatmap`):
 * StatusStrip cells size from their content, so empty cells need `h-8`, and
 * the DS `neutral` tone is nearly invisible on the dark theme, so no-data
 * cells get `bg-border`. Both are token-backed utilities passed via
 * `className`; drop them if StatusStrip gains a dense variant.
 */

import { Card, cn, EmptyState, Skeleton, StatusStrip, Typography } from "@norican/react";
import { Link } from "react-router";

import type { DayUptime, MonitoringHistory } from "@/lib/api";

import type { FeedState } from "./feed-state";

/** Availability thresholds for a site-day — kept in sync with the Websites board. */
const HEALTHY_PCT = 99.5;
const SHAKY_PCT = 95;

type CellStatus = "positive" | "caution" | "negative" | "neutral";

function siteDayStatus(day: DayUptime): CellStatus {
  if (!day.checks || day.uptime_pct == null) {
    return "neutral";
  }
  if (day.uptime_pct >= HEALTHY_PCT) {
    return "positive";
  }
  if (day.uptime_pct >= SHAKY_PCT) {
    return "caution";
  }
  return "negative";
}

function formatDay(iso: string): string {
  const at = new Date(`${iso}T00:00:00Z`);
  // An unparseable date renders as the literal "Invalid Date" otherwise, which
  // then sits on an axis or a caption looking like a value. `src/features/
  // security/status.ts` already guards this way; this matches it.
  if (Number.isNaN(at.getTime())) {
    return "—";
  }
  return at.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

interface PulseDay {
  day: string;
  status: CellStatus;
  title: string;
}

/**
 * Fold the per-site series into one worst-status cell per day. The backend
 * emits a dense, aligned window per site (every day present, `checks: 0` for
 * unobserved days), so folding by index is safe.
 */
function worstPerDay(history: MonitoringHistory): PulseDay[] {
  const firstSiteDays = history.sites[0]?.days ?? [];
  const days: PulseDay[] = [];

  for (let index = 0; index < firstSiteDays.length; index += 1) {
    const iso = firstSiteDays[index]?.day;
    if (iso === undefined) {
      continue;
    }
    let failing = 0;
    let shaky = 0;
    let healthy = 0;

    for (const site of history.sites) {
      const day = site.days[index];
      if (day === undefined) {
        continue; // Defensive — the backend emits aligned, dense windows.
      }
      const status = siteDayStatus(day);
      if (status === "negative") {
        failing += 1;
      } else if (status === "caution") {
        shaky += 1;
      } else if (status === "positive") {
        healthy += 1;
      }
    }

    const observed = failing + shaky + healthy;
    const status: CellStatus =
      observed === 0 ? "neutral" : failing > 0 ? "negative" : shaky > 0 ? "caution" : "positive";

    const title =
      observed === 0
        ? `${formatDay(iso)} — no checks recorded`
        : status === "positive"
          ? `${formatDay(iso)} — all ${observed} checked sites healthy`
          : `${formatDay(iso)} — ${failing} failing · ${shaky} shaky · ${healthy} healthy (${observed} sites checked)`;

    days.push({ day: iso, status, title });
  }

  return days;
}

interface EstatePulseProps {
  history: FeedState<MonitoringHistory>;
}

export function EstatePulse({ history }: EstatePulseProps) {
  const data = history.data;

  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography variant="heading-4" as="h2">
            Estate pulse · last 14 days
          </Typography>
          <Link to="/websites" className="text-fg-muted hover:text-fg text-sm">
            Websites board →
          </Link>
        </div>
      </Card.Header>
      <Card.Body>
        {history.isError ? (
          <EmptyState
            size="sm"
            title="Availability history unavailable"
            description="The NOVA API did not respond — this is a NOVA problem, not a websites one."
          />
        ) : data === undefined ? (
          <Skeleton height="6rem" />
        ) : data.sites.length === 0 ? (
          <EmptyState
            size="sm"
            title="No sites monitored"
            description="The website poller has no targets configured."
          />
        ) : (
          <PulseStrip history={data} />
        )}
      </Card.Body>
    </Card>
  );
}

function PulseStrip({ history }: { history: MonitoringHistory }) {
  const days = worstPerDay(history);
  const recordedDays = days.filter((day) => day.status !== "neutral").length;

  return (
    <div className="flex flex-col gap-2">
      <StatusStrip aria-label="Worst website status per day, last 14 days">
        {days.map((day) => (
          <StatusStrip.Cell
            key={day.day}
            status={day.status}
            title={day.title}
            // See the WORKAROUND note in the file header.
            className={cn("h-8", day.status === "neutral" && "bg-border")}
          />
        ))}
      </StatusStrip>

      <div className="flex items-baseline justify-between gap-4">
        <Typography variant="caption" as="span">
          {history.days} days ago
        </Typography>
        {/* `tone="subtle"` is documented as never meeting 4.5:1 below 18px, so
            this coverage line stays `muted` — it is the sample-size caption and
            has to stay readable. */}
        <Typography variant="caption" as="span" tone="muted">
          Worst status across {history.sites.length} website checks · {recordedDays} of{" "}
          {history.days} days recorded
        </Typography>
        <Typography variant="caption" as="span">
          Today
        </Typography>
      </div>

      <div className="text-fg-muted flex flex-wrap items-center gap-x-5 gap-y-2 pt-1 text-xs">
        <LegendKey status="positive" label="All sites healthy" />
        <LegendKey status="caution" label={`A site below ${HEALTHY_PCT}%`} />
        <LegendKey status="negative" label={`A site below ${SHAKY_PCT}%`} />
        <LegendKey status="neutral" label="No data" />
      </div>
    </div>
  );
}

function LegendKey({ status, label }: { status: CellStatus; label: string }) {
  return (
    <span className="flex items-center gap-2">
      {/* A single-cell strip reuses the exact tints the bar above uses. */}
      <span className="inline-block w-6">
        <StatusStrip aria-hidden="true">
          <StatusStrip.Cell
            status={status}
            className={cn("h-3", status === "neutral" && "bg-border")}
          />
        </StatusStrip>
      </span>
      {label}
    </span>
  );
}
